import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Space,
  Modal,
  Upload,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { BodyParts } from '../../../../types/BodyParts';

const { TextArea } = Input;
interface BodyPartsModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: () => void;
  initialValues?: BodyParts | null;
  isEditMode?: boolean;
  bodyParts: BodyParts[];
}

const BodyPartsModal: React.FC<BodyPartsModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
  bodyParts,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);
  

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);


  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');


  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };

  // image preview 
  const handlePreview = async (file: UploadFile) => {
      if (!file.url && !file.preview) {
          file.preview = await getBase64(file.originFileObj as File);
      }

      setPreviewImage(file.url || (file.preview as string));
      setPreviewOpen(true);
      setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  // Utility function to convert image to base64
  const getBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = error => reject(error);
      });


  const handleSubmit = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('bp_unit', values.bp_unit);
      formData.append('bp_name', values.bp_name);
      formData.append('bp_video', values.bp_video);
      formData.append('bp_instructions', values.bp_instructions);

      if (imageFile) {
        formData.append('attachments', imageFile);
      }

      if (initialValues?.bp_id) {
        formData.append('bp_id', initialValues.bp_id.toString());
      }

      const url = isEditMode ? 'edit-body-part' : 'add-body-part';
      const method = isEditMode ? 'put' : 'post';

      await apiRequest({
        method,
        url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onCreate();
      showSuccess(isEditMode ? 'Body Part updated successfully!' : 'Body Part created successfully!');
      form.resetFields();
      if (!isModalClose) {
            setImageFile(null);
            setImageFileList([]);
        }

      if (isModalClose) {
        onCancel();
      }
    } catch {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} body part`);
    }
  };

  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        bp_name: initialValues.bp_name,
        bp_unit: initialValues.bp_unit,
        bp_instructions: initialValues.bp_instructions,
        bp_video: initialValues.bp_video,
      });
      if (initialValues.bp_image) {
        setImageFileList([
          {
            uid: '-1',
            name: 'image.png',
            status: 'done',
            url: initialValues.bp_image,
          },
        ]);
        setImageFile(null);
      }
    } else if (visible) {
      form.resetFields();
      setImageFileList([]);
      setImageFile(null);
    }
  }, [visible, isEditMode, initialValues, form]);

  const uploadButton = (
    <div
      style={{
        width: 220,
        height: 220,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <PlusOutlined style={{ fontSize: 24 }} />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
      <>
        <Modal
          title={isEditMode ? 'Edit Body Part' : 'Add New Body Part'}
          visible={visible}
          onCancel={onCancel}
          footer={null}
          width="90%"
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="bp_name"
                  className="px-8"
                  label={
                    <span>
                      Body Part Name&nbsp;
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        (Max 100 characters)
                      </span>
                    </span>
                  }
                  rules={[
                    { required: true, message: 'Please enter the body part name' },
                    { max: 100, message: 'Maximum 100 characters allowed' },
                  ]}
                >
                  <Input
                    showCount
                    maxLength={100}
                    placeholder="e.g., Body Name"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="bp_unit"
                  className="px-8"
                  label={
                    <span>
                      Unit&nbsp;
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        (Max 10 characters)
                      </span>
                    </span>
                  }
                  rules={[
                    { required: true, message: 'Please enter the Unit' },
                    { max: 10, message: 'Maximum 10 characters allowed' },
                  ]}
                >
                  <Input
                    showCount
                    maxLength={10}
                    placeholder="e.g., Unit"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="bp_video"
                  className="px-8"
                  label={
                    <span>
                      Video Link &nbsp;
                      <span style={{ fontSize: '12px', color: '#888' }}>
                         (Max 100 characters)
                      </span>
                    </span>
                  }
                  rules={[
                    { max: 100, message: 'Maximum 100 characters allowed' },
                  ]}
                >
                  <Input
                    showCount
                    maxLength={100}
                    placeholder="e.g., youtube video link"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                    name="bp_instructions"
                    className="px-8"
                    label="Instructions"
                    rules={[
                      { max: 1000, message: 'Maximum 1000 characters allowed' },
                    ]}
                  >
                    <TextArea
                      placeholder="Enter Instructions"
                      maxLength={1000}    // Limit to 1000 chars
                      showCount            // Show current / max count
                      rows={4}    
                      autoSize={{ minRows: 4, maxRows: 8 }}         // Initial visible rows
                    />
                  </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Upload Image" required>
                  <Upload
                    beforeUpload={() => false}
                    listType="picture-card"
                    maxCount={1}
                    fileList={imageFileList}
                    onPreview={handlePreview} // 👈 Add this
                    onChange={({ fileList }) => {
                      setImageFileList(fileList);
                      const rawFile = fileList[0]?.originFileObj;
                      if (rawFile instanceof File) {
                        setImageFile(rawFile);
                      } else {
                        setImageFile(null);
                      }
                    }}
                  >
                    {imageFileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Space>
                <Button onClick={onCancel}>Cancel</Button>
                {!isEditMode && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    onClick={() => setIsModalClose(false)}
                    disabled={isSubmitDisabled()}
                  >
                    Submit & Create New Body Part
                  </Button>
                )}
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={() => setIsModalClose(true)}
                  disabled={isSubmitDisabled()}
                >
                  Submit
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
        <Modal
            visible={previewOpen}
            title={previewTitle}
            footer={null}
            centered
            onCancel={() => setPreviewOpen(false)}
        >
            <img
                alt="Preview"
                style={{ width: '100%' }}
                src={previewImage}
        />
        </Modal>
      </>
  );
};

export default BodyPartsModal;
